import React, { useState, useEffect } from "react";
import { PageHero } from "@/components/common/Bits";
import { useLanguage } from "@/context/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function Field({ label, name, type = "text", value, onChange, required, testid, placeholder, options, isTextArea }) {
  const commonCls = "w-full px-4 py-3 rounded-lg border border-[#E5E7EB] bg-white text-[#102A43] focus:border-[#1F8A5B] focus:outline-none focus:ring-2 focus:ring-[#1F8A5B]/10 transition-colors text-sm";
  return (
    <div>
      <label className="block text-sm font-medium text-[#102A43] mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {isTextArea ? (
        <textarea name={name} value={value} onChange={onChange} required={required} placeholder={placeholder} rows={4} className={commonCls} data-testid={testid}/>
      ) : options ? (
        <select name={name} value={value} onChange={onChange} required={required} className={commonCls} data-testid={testid}>
          <option value="">-- Seçiniz --</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder} className={commonCls} data-testid={testid}/>
      )}
    </div>
  );
}

function CheckboxGroup({ label, name, values, options, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#102A43] mb-2">{label}</label>
      <div className="grid grid-cols-2 gap-2" data-testid={testid}>
        {options.map(o => (
          <label key={o.value} className="flex items-center gap-2 text-sm text-[#102A43] cursor-pointer">
            <input
              type="checkbox"
              checked={values.includes(o.value)}
              onChange={() => {
                const next = values.includes(o.value) ? values.filter(v => v !== o.value) : [...values, o.value];
                onChange(name, next);
              }}
              className="w-4 h-4 rounded border-[#E5E7EB] text-[#1F8A5B] focus:ring-[#1F8A5B]"
              data-testid={`${testid}-${o.value}`}
            />
            {o.label}
          </label>
        ))}
      </div>
    </div>
  );
}

/* MEMBERSHIP FORM */
export function MembershipPage() {
  const { t } = useLanguage();
  const [f, setF] = useState({
    membership_type: "", name: "", company_name: "", title: "", phone: "", email: "",
    city: "", country: "", sector: "", website: "", activity_area: "",
    description: "", international_experience: "", interested_fields: [],
    kvkk_approved: false, consent_approved: false,
  });
  const [state, setState] = useState({ loading: false, ok: false, err: null });

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const onChange = (e) => set(e.target.name, e.target.type === "checkbox" ? e.target.checked : e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.kvkk_approved) { toast.error("KVKK onayı gereklidir"); return; }
    setState({ loading: true, ok: false, err: null });
    try {
      await api.post("/applications/membership", f);
      setState({ loading: false, ok: true, err: null });
      toast.success(t.form_submitted);
      setF(prev => ({ ...prev, name: "", company_name: "", title: "", phone: "", email: "", description: "" }));
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setState({ loading: false, ok: false, err: msg });
      toast.error(msg);
    }
  };

  const interestedOptions = [
    { value: "industry", label: t.iarea_industry },
    { value: "education", label: t.iarea_education },
    { value: "social", label: t.iarea_social },
    { value: "culture", label: t.iarea_culture },
    { value: "research", label: t.iarea_research },
    { value: "financial", label: t.iarea_financial },
    { value: "volunteer", label: t.iarea_volunteer },
  ];

  return (
    <div data-testid="page-membership">
      <PageHero overline={t.ov_membership} title={t.membership_hero_title} description={t.membership_hero_desc} image="https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"/>
      <section className="py-16 useko-container max-w-3xl">
        <form onSubmit={submit} className="space-y-5" data-testid="membership-form">
          <Field label={t.form_membership_type} name="membership_type" value={f.membership_type} onChange={onChange} required testid="input-membership-type" options={[
            { value: "individual", label: t.mtype_individual },
            { value: "corporate", label: t.mtype_corporate },
            { value: "volunteer_member", label: t.mtype_volunteer },
            { value: "supporter", label: t.mtype_supporter },
          ]}/>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label={t.form_name} name="name" value={f.name} onChange={onChange} required testid="input-name"/>
            <Field label={t.form_email} type="email" name="email" value={f.email} onChange={onChange} required testid="input-email"/>
            <Field label={t.form_company} name="company_name" value={f.company_name} onChange={onChange} testid="input-company"/>
            <Field label={t.form_title} name="title" value={f.title} onChange={onChange} testid="input-title"/>
            <Field label={t.form_phone} name="phone" value={f.phone} onChange={onChange} testid="input-phone"/>
            <Field label={t.form_website} name="website" value={f.website} onChange={onChange} testid="input-website"/>
            <Field label={t.form_city} name="city" value={f.city} onChange={onChange} testid="input-city"/>
            <Field label={t.form_country} name="country" value={f.country} onChange={onChange} testid="input-country"/>
            <Field label={t.form_sector} name="sector" value={f.sector} onChange={onChange} testid="input-sector"/>
            <Field label={t.form_activity_area} name="activity_area" value={f.activity_area} onChange={onChange} testid="input-activity-area"/>
          </div>
          <Field label={t.form_intl_experience} name="international_experience" value={f.international_experience} onChange={onChange} testid="input-intl-experience" isTextArea/>
          <Field label={t.form_desc} name="description" value={f.description} onChange={onChange} testid="input-description" isTextArea/>
          <CheckboxGroup label={t.form_interested_fields} name="interested_fields" values={f.interested_fields} options={interestedOptions} onChange={set} testid="checkbox-interested"/>

          <div className="space-y-2 pt-3 border-t border-[#E5E7EB]">
            <label className="flex items-start gap-2 text-sm text-[#102A43] cursor-pointer">
              <input type="checkbox" name="kvkk_approved" checked={f.kvkk_approved} onChange={onChange} className="mt-1 w-4 h-4 rounded border-[#E5E7EB] text-[#1F8A5B] focus:ring-[#1F8A5B]" data-testid="input-kvkk"/>
              <span>{t.form_kvkk} <a href="/kvkk" target="_blank" className="text-[#1F8A5B] underline">Detaylar</a></span>
            </label>
            <label className="flex items-start gap-2 text-sm text-[#102A43] cursor-pointer">
              <input type="checkbox" name="consent_approved" checked={f.consent_approved} onChange={onChange} className="mt-1 w-4 h-4 rounded border-[#E5E7EB] text-[#1F8A5B] focus:ring-[#1F8A5B]" data-testid="input-consent"/>
              <span>{t.form_consent}</span>
            </label>
          </div>

          {state.ok && <div className="p-4 rounded-lg bg-[#EAF7F0] border border-[#1F8A5B]/20 flex items-start gap-3 text-sm text-[#0F5132]"><CheckCircle2 className="w-5 h-5 flex-shrink-0"/>{t.form_submitted}</div>}
          {state.err && <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3 text-sm text-red-800"><AlertCircle className="w-5 h-5 flex-shrink-0"/>{state.err}</div>}

          <button type="submit" disabled={state.loading} className="btn-primary w-full justify-center" data-testid="submit-membership">
            {state.loading ? t.loading : t.btn_submit}
          </button>
        </form>
      </section>
    </div>
  );
}

/* VOLUNTEER FORM */
export function VolunteerPage() {
  const { t } = useLanguage();
  const [f, setF] = useState({
    name: "", phone: "", email: "", city: "", profession: "", age_range: "",
    volunteer_fields: [], available_days: [], experience: "", message: "",
    kvkk_approved: false,
  });
  const [state, setState] = useState({ loading: false, ok: false, err: null });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const onChange = (e) => set(e.target.name, e.target.type === "checkbox" ? e.target.checked : e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.kvkk_approved) { toast.error("KVKK onayı gereklidir"); return; }
    setState({ loading: true, ok: false, err: null });
    try {
      await api.post("/applications/volunteer", f);
      setState({ loading: false, ok: true, err: null });
      toast.success(t.form_submitted);
      setF(prev => ({ ...prev, name: "", phone: "", email: "", message: "" }));
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setState({ loading: false, ok: false, err: msg });
      toast.error(msg);
    }
  };

  const fields = [
    { value: "education", label: t.vfield_education }, { value: "research", label: t.vfield_research },
    { value: "trade", label: t.vfield_trade }, { value: "logistics", label: t.vfield_logistics },
    { value: "social_media", label: t.vfield_social_media }, { value: "translation", label: t.vfield_translation },
    { value: "field", label: t.vfield_field }, { value: "organization", label: t.vfield_organization },
    { value: "data", label: t.vfield_data },
  ];
  const days = [
    { value: "mon", label: t.day_mon }, { value: "tue", label: t.day_tue }, { value: "wed", label: t.day_wed },
    { value: "thu", label: t.day_thu }, { value: "fri", label: t.day_fri }, { value: "sat", label: t.day_sat },
    { value: "sun", label: t.day_sun },
  ];

  return (
    <div data-testid="page-volunteer">
      <PageHero overline={t.ov_volunteer} title={t.volunteer_hero_title} description={t.volunteer_hero_desc} image="https://images.unsplash.com/photo-1628717341663-0007b0ee2597?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"/>
      <section className="py-16 useko-container max-w-3xl">
        <form onSubmit={submit} className="space-y-5" data-testid="volunteer-form">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label={t.form_name} name="name" value={f.name} onChange={onChange} required testid="input-name"/>
            <Field label={t.form_email} type="email" name="email" value={f.email} onChange={onChange} required testid="input-email"/>
            <Field label={t.form_phone} name="phone" value={f.phone} onChange={onChange} testid="input-phone"/>
            <Field label={t.form_city} name="city" value={f.city} onChange={onChange} testid="input-city"/>
            <Field label={t.form_profession} name="profession" value={f.profession} onChange={onChange} testid="input-profession"/>
            <Field label={t.form_age_range} name="age_range" value={f.age_range} onChange={onChange} testid="input-age-range" options={[
              { value: "18-25", label: "18-25" }, { value: "26-35", label: "26-35" },
              { value: "36-50", label: "36-50" }, { value: "51+", label: "51+" },
            ]}/>
          </div>
          <CheckboxGroup label={t.form_volunteer_fields} name="volunteer_fields" values={f.volunteer_fields} options={fields} onChange={set} testid="checkbox-fields"/>
          <CheckboxGroup label={t.form_available_days} name="available_days" values={f.available_days} options={days} onChange={set} testid="checkbox-days"/>
          <Field label={t.form_experience} name="experience" value={f.experience} onChange={onChange} testid="input-experience" isTextArea/>
          <Field label={t.form_message} name="message" value={f.message} onChange={onChange} testid="input-message" isTextArea/>

          <label className="flex items-start gap-2 text-sm text-[#102A43] cursor-pointer pt-3 border-t border-[#E5E7EB]">
            <input type="checkbox" name="kvkk_approved" checked={f.kvkk_approved} onChange={onChange} className="mt-1 w-4 h-4 rounded border-[#E5E7EB] text-[#1F8A5B] focus:ring-[#1F8A5B]" data-testid="input-kvkk"/>
            <span>{t.form_kvkk}</span>
          </label>

          {state.ok && <div className="p-4 rounded-lg bg-[#EAF7F0] border border-[#1F8A5B]/20 flex items-start gap-3 text-sm text-[#0F5132]"><CheckCircle2 className="w-5 h-5 flex-shrink-0"/>{t.form_submitted}</div>}
          {state.err && <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3 text-sm text-red-800"><AlertCircle className="w-5 h-5 flex-shrink-0"/>{state.err}</div>}

          <button type="submit" disabled={state.loading} className="btn-primary w-full justify-center" data-testid="submit-volunteer">
            {state.loading ? t.loading : t.btn_submit}
          </button>
        </form>
      </section>
    </div>
  );
}

/* PARTNERSHIP FORM */
export function PartnershipPage() {
  const { t } = useLanguage();
  const [f, setF] = useState({
    company_name: "", contact_name: "", title: "", phone: "", email: "",
    website: "", country: "", city: "", sector: "", partnership_type: "",
    interested_area: "", description: "", reference_projects: "",
    kvkk_approved: false,
  });
  const [state, setState] = useState({ loading: false, ok: false, err: null });
  const onChange = (e) => setF(prev => ({ ...prev, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.kvkk_approved) { toast.error("KVKK onayı gereklidir"); return; }
    setState({ loading: true, ok: false, err: null });
    try {
      await api.post("/applications/partnership", f);
      setState({ loading: false, ok: true, err: null });
      toast.success(t.form_submitted);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setState({ loading: false, ok: false, err: msg });
      toast.error(msg);
    }
  };

  return (
    <div data-testid="page-partnership">
      <PageHero overline={t.ov_corporate} title={t.partnership_hero_title} description={t.partnership_hero_desc} image="https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"/>
      <section className="py-16 useko-container max-w-3xl">
        <form onSubmit={submit} className="space-y-5" data-testid="partnership-form">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label={t.form_company} name="company_name" value={f.company_name} onChange={onChange} required testid="input-company"/>
            <Field label={t.form_contact_person || "Yetkili Kişi"} name="contact_name" value={f.contact_name} onChange={onChange} required testid="input-contact-name"/>
            <Field label={t.form_title} name="title" value={f.title} onChange={onChange} testid="input-title"/>
            <Field label={t.form_email} type="email" name="email" value={f.email} onChange={onChange} required testid="input-email"/>
            <Field label={t.form_phone} name="phone" value={f.phone} onChange={onChange} testid="input-phone"/>
            <Field label={t.form_website} name="website" value={f.website} onChange={onChange} testid="input-website"/>
            <Field label={t.form_country} name="country" value={f.country} onChange={onChange} testid="input-country"/>
            <Field label={t.form_city} name="city" value={f.city} onChange={onChange} testid="input-city"/>
            <Field label={t.form_sector} name="sector" value={f.sector} onChange={onChange} testid="input-sector"/>
            <Field label={t.form_partnership_type} name="partnership_type" value={f.partnership_type} onChange={onChange} required testid="input-partnership-type" options={[
              { value: "project", label: t.ptype_project },
              { value: "sponsorship", label: t.ptype_sponsorship },
              { value: "support", label: t.ptype_support },
              { value: "trade", label: t.ptype_trade },
              { value: "research", label: t.ptype_research },
              { value: "b2b", label: t.ptype_b2b },
              { value: "diplomatic", label: t.ptype_diplomatic },
            ]}/>
          </div>
          <Field label={t.form_interested_area || "İlgilenilen Alan"} name="interested_area" value={f.interested_area} onChange={onChange} testid="input-interested-area"/>
          <Field label={t.form_project_description || "Proje / Talep Açıklaması"} name="description" value={f.description} onChange={onChange} testid="input-description" isTextArea/>
          <Field label={t.form_reference_projects} name="reference_projects" value={f.reference_projects} onChange={onChange} testid="input-reference" isTextArea/>

          <label className="flex items-start gap-2 text-sm text-[#102A43] cursor-pointer pt-3 border-t border-[#E5E7EB]">
            <input type="checkbox" name="kvkk_approved" checked={f.kvkk_approved} onChange={onChange} className="mt-1 w-4 h-4 rounded border-[#E5E7EB] text-[#1F8A5B] focus:ring-[#1F8A5B]" data-testid="input-kvkk"/>
            <span>{t.form_kvkk}</span>
          </label>

          {state.ok && <div className="p-4 rounded-lg bg-[#EAF7F0] border border-[#1F8A5B]/20 flex items-start gap-3 text-sm text-[#0F5132]"><CheckCircle2 className="w-5 h-5 flex-shrink-0"/>{t.form_submitted}</div>}
          {state.err && <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3 text-sm text-red-800"><AlertCircle className="w-5 h-5 flex-shrink-0"/>{state.err}</div>}

          <button type="submit" disabled={state.loading} className="btn-primary w-full justify-center" data-testid="submit-partnership">
            {state.loading ? t.loading : t.btn_submit}
          </button>
        </form>
      </section>
    </div>
  );
}

/* CONTACT PAGE */
export function ContactPage() {
  const { t } = useLanguage();
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "", message: "", kvkk_approved: false });
  const [state, setState] = useState({ loading: false, ok: false, err: null });
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get(`/settings?_=${Date.now()}`).then(r => setSettings(r.data || {})).catch(()=>{}); }, []);
  const onChange = (e) => setF(prev => ({ ...prev, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.kvkk_approved) { toast.error("KVKK onayı gereklidir"); return; }
    setState({ loading: true, ok: false, err: null });
    try {
      await api.post("/contact", f);
      setState({ loading: false, ok: true, err: null });
      toast.success(t.form_submitted);
      setF({ name: "", email: "", phone: "", subject: "", message: "", kvkk_approved: false });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail);
      setState({ loading: false, ok: false, err: msg });
      toast.error(msg);
    }
  };

  return (
    <div data-testid="page-contact">
      <PageHero overline={t.nav_contact} title={t.contact_hero_title} description={t.contact_hero_desc}/>
      <section className="py-16 useko-container grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="p-8 rounded-2xl bg-[#F7F9FA] space-y-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4B5563] font-medium">{t.contact_addr_label || "Adres"}</div>
              <div className="mt-1 font-medium text-[#102A43]">{settings.contact_address || t.contact_addr_line}</div>
            </div>
            {(settings.contact_phone || "+90 216 519 19 53") && (
              <div>
                <div className="text-xs uppercase tracking-widest text-[#4B5563] font-medium">{t.contact_phone_label || "Telefon"}</div>
                <div className="mt-1 font-medium text-[#102A43]">{settings.contact_phone || "+90 216 519 19 53"}</div>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4B5563] font-medium">{t.contact_email_label || "E-posta"}</div>
              <div className="mt-1 font-medium text-[#102A43]">{settings.contact_email || "info@useko.org"}</div>
            </div>
          </div>
          <div className="mt-4 aspect-video rounded-2xl overflow-hidden">
            <iframe title="map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24080.28234!2d28.9789!3d41.0082!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14caa7040068086b%3A0xe1ccfe98bc01b0d0!2sIstanbul%2C%20Turkey!5e0!3m2!1sen!2s!4v1710000000000" width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy"></iframe>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-7 space-y-5" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label={t.form_name} name="name" value={f.name} onChange={onChange} required testid="contact-name"/>
            <Field label={t.form_email} type="email" name="email" value={f.email} onChange={onChange} required testid="contact-email"/>
            <Field label={t.form_phone} name="phone" value={f.phone} onChange={onChange} testid="contact-phone"/>
            <Field label={t.form_subject} name="subject" value={f.subject} onChange={onChange} testid="contact-subject"/>
          </div>
          <Field label={t.form_message} name="message" value={f.message} onChange={onChange} required isTextArea testid="contact-message"/>
          <label className="flex items-start gap-2 text-sm text-[#102A43] cursor-pointer">
            <input type="checkbox" name="kvkk_approved" checked={f.kvkk_approved} onChange={onChange} className="mt-1 w-4 h-4 rounded border-[#E5E7EB] text-[#1F8A5B]" data-testid="contact-kvkk"/>
            <span>{t.form_kvkk}</span>
          </label>
          {state.ok && <div className="p-4 rounded-lg bg-[#EAF7F0] border border-[#1F8A5B]/20 text-sm text-[#0F5132] flex items-start gap-3"><CheckCircle2 className="w-5 h-5"/>{t.form_submitted}</div>}
          {state.err && <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-3"><AlertCircle className="w-5 h-5"/>{state.err}</div>}
          <button type="submit" disabled={state.loading} className="btn-primary" data-testid="submit-contact">{state.loading ? t.loading : t.btn_send}</button>
        </form>
      </section>
    </div>
  );
}
